/**
 * Shared types for WhoHasAccessDialog and its sub-components (AddPicker).
 * Kept in their own module so the dialog and the picker can both import
 * without forming an import cycle.
 */
import type { MiniappIconSpec } from '@papan/types'

export type Permission = 'hidden' | 'readcopy' | 'readwrite' | 'admin'

export type TagType = 'role' | 'team' | 'member' | 'app'

export interface AccessTag {
  type: TagType
  /** Role name | team id | email | app (.miniapp folder) id */
  targetId: string
  /** Display text (no prefix for member, prefixed for role/team) */
  label: string
  /** Currently-resolved permission (where this tag lives in the sidebar) */
  permission: Permission
  inherited: boolean
  /** Closest ancestor path that supplied the inherited permission, if known. */
  inheritedFromPath?: string
  /** folder_permissions row id — present iff `!inherited`. Needed for DELETE. */
  permId?: string
  /** Team is granted on this folder but its name/membership isn't visible to
   *  the caller (caller isn't related to the team via the privacy filter).
   *  Card renders "Redacted" in muted color, subline + tooltip suppressed. */
  redacted?: boolean
  /** App-principal (`type: 'app'`) only — the disambiguating parent-folder
   *  label (subline) and the full app-folder path (tooltip). `targetId` holds
   *  the `.miniapp` folder id used to route grant/retier/revoke through the
   *  mini-app access API (which also manages the `.chain`). */
  appPath?: string
  appFullPath?: string
  /** App-principal only — the app's OWN icon (spec + cache rev), so its card shows
   *  the icon the drive shows. Absent ⇒ the name-derived default, as everywhere. */
  appIcon?: MiniappIconSpec | null
  appIconRev?: string | null
}

export interface TeamInfo {
  id: string
  name: string
  /** Parent team id — null for top-level teams. Used to render sub-teams. */
  parentId?: string | null
  /** Total recursive member count (server-side via teamsRepo.previewAllMembers). */
  memberCount?: number
  /** Top-5 member preview (`Name <email>` or just `email`). */
  memberPreview?: string[]
}
/**
 * `status` mirrors `GET /api/orgs/:id/members`, which returns members
 * (`active`) AND pending invites (`invited` / `declined`). An invitee has no
 * membership row yet, but the folder-permission routes accept one as a grant
 * TARGET on purpose, so access can be staged before they accept (issue #112).
 * Optional: older call sites build bare members and mean `active`.
 */
export interface MemberInfo { email: string; name: string; role: string; status?: 'active' | 'invited' | 'declined' }

export const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  admin: 'Drive admin',
  member: 'Member',
  viewer: 'Viewer',
  guest: 'Guest',
  noaccess: 'No Access',
}
